import keyBy from 'lodash/keyBy';
import type { RootState } from '../reducers';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { GlobalDto } from '../reducers/global/globalReducerTypes';
import type { FunnelMapWithFields, FunnelWithMaybeFields } from './types';
import type { SweepFieldsSummaryMap } from '../types/fields';
import { SfdcDisplayType } from '../types/fields';
import type { SweepFieldSummary } from '../types/fields';
import type { SalesforceFieldType } from 'ts-force';
import type { DedupMatchingDto } from '../types/DedupMatching';

export interface SweepFieldInfo {
  label?: string;
  objectName?: string;
  name?: string;
  sfType?: SalesforceFieldType;
  sfDisplayType?: SfdcDisplayType;
  isRestrictedPicklist?: boolean;
  isRequired?: boolean;
  referenceTo: string[];
}

export type SweepFieldsInfoMap = { [id: string]: SweepFieldInfo };

export interface SweepFieldsReducerState {
  sweepFieldsInfo: SweepFieldsInfoMap;
}

const initialState: SweepFieldsReducerState = {
  sweepFieldsInfo: {},
};

const extractInfoFromSweepField = (
  sweepFieldPick: SweepFieldSummary | SweepField,
): SweepFieldInfo => {
  if ('isRestrictedPicklist' in sweepFieldPick) {
    return {
      label: sweepFieldPick.sweepFieldName,
      objectName: sweepFieldPick.objectName,
      name: sweepFieldPick.sfFieldName,
      sfType: sweepFieldPick.sfField?.type,
      sfDisplayType: sweepFieldPick.sfDisplayType,
      isRestrictedPicklist: sweepFieldPick.isRestrictedPicklist,
      referenceTo: sweepFieldPick.sfField?.referenceTo || [],
    };
  }
  const _sweepField = sweepFieldPick as SweepField;
  const isRestrictedPicklist =
    [SfdcDisplayType.PICKLIST, SfdcDisplayType.MULTIPICKLIST].includes(_sweepField.sfDisplayType) &&
    _sweepField.sfField.restrictedPicklist;

  return {
    label: sweepFieldPick.sweepFieldName,
    objectName: sweepFieldPick.objectName,
    name: sweepFieldPick.sfFieldName,
    sfType: sweepFieldPick.sfField?.type,
    sfDisplayType: sweepFieldPick.sfDisplayType,
    isRestrictedPicklist,
    referenceTo: _sweepField.sfField.referenceTo || [],
  };
};

const extractInfoFromSweepFields = (sweepFieldsMap: SweepFieldsSummaryMap): SweepFieldsInfoMap => {
  return Object.keys(sweepFieldsMap).reduce<SweepFieldsInfoMap>((acc, id) => {
    acc[id] = extractInfoFromSweepField(sweepFieldsMap[id]);
    return acc;
  }, {});
};
export const sweepFieldsInfoSlice = createSlice({
  name: 'sweepFieldsInfo',
  initialState,
  reducers: {
    populateFieldsInfoFromFunnelMap: (
      state,
      action: PayloadAction<{ funnelMap: FunnelMapWithFields }>,
    ) => {
      const { funnelsData } = action.payload.funnelMap;
      if (!funnelsData) {
        return;
      }

      Object.values(funnelsData).forEach((funnelData) => {
        if (funnelData.sweepFields) {
          Object.assign(state.sweepFieldsInfo, extractInfoFromSweepFields(funnelData.sweepFields));
        }
      });
    },
    populateFieldsInfoFromGlobalDTO: (state, action: PayloadAction<{ globalDto: GlobalDto }>) => {
      Object.assign(
        state.sweepFieldsInfo,
        extractInfoFromSweepFields(action.payload.globalDto.sweepFields),
      );
    },
    populateFieldsInfoFromFunnelMaps: (
      state,
      action: PayloadAction<{ funnelMaps: FunnelMapWithFields[] }>,
    ) => {
      const { funnelMaps } = action.payload;

      Object.values(funnelMaps).forEach(({ funnelsData }) => {
        if (funnelsData) {
          Object.values(funnelsData).forEach((funnelData) => {
            if (funnelData.sweepFields) {
              Object.assign(
                state.sweepFieldsInfo,
                extractInfoFromSweepFields(funnelData.sweepFields),
              );
            }
          });
        }
      });
    },
    populateFieldsInfoFromFunnel: (
      state,
      action: PayloadAction<{ funnel: FunnelWithMaybeFields }>,
    ) => {
      const { sweepFields } = action.payload.funnel;
      if (sweepFields) {
        Object.assign(state.sweepFieldsInfo, extractInfoFromSweepFields(sweepFields));
      }
    },
    populateFieldsInfoFromFunnels: (
      state,
      action: PayloadAction<{ funnels: FunnelWithMaybeFields[] }>,
    ) => {
      const { funnels } = action.payload;
      Object.values(funnels).forEach(({ sweepFields }) => {
        if (sweepFields) {
          Object.assign(state.sweepFieldsInfo, extractInfoFromSweepFields(sweepFields));
        }
      });
    },
    populateFieldsInfoFromSweepFields: (
      state,
      action: PayloadAction<{ sweepFields: SweepField[] }>,
    ) => {
      const { sweepFields } = action.payload;
      const sweepFieldById = keyBy(sweepFields, 'id');

      Object.assign(state.sweepFieldsInfo, extractInfoFromSweepFields(sweepFieldById));
    },
    populateFieldsInfoFromDedupMatchingList: (
      state,
      action: PayloadAction<{ dedupMatchingList: DedupMatchingDto[] }>,
    ) => {
      const { dedupMatchingList } = action.payload;
      dedupMatchingList.forEach((dedupMatching) => {
        if (dedupMatching.sweepFields) {
          Object.assign(
            state.sweepFieldsInfo,
            extractInfoFromSweepFields(dedupMatching.sweepFields),
          );
        }
      });
    },
    populateFieldsInfo: (
      state,
      action: PayloadAction<{ id: string; labelInfo: SweepFieldInfo }[]>,
    ) => {
      const { payload } = action;
      payload.forEach(({ id, labelInfo }) => {
        state.sweepFieldsInfo[id] = labelInfo;
      });
    },
    populateFieldsInfoFromSummary: (
      state,
      action: PayloadAction<{ sweepFields: SweepFieldsSummaryMap }>,
    ) => {
      const { sweepFields } = action.payload;
      Object.assign(state.sweepFieldsInfo, extractInfoFromSweepFields(sweepFields));
    },
  },
});

export const {
  populateFieldsInfoFromFunnelMap,
  populateFieldsInfoFromFunnelMaps,
  populateFieldsInfoFromFunnel,
  populateFieldsInfoFromFunnels,
  populateFieldsInfoFromSweepFields,
  populateFieldsInfoFromDedupMatchingList,
  populateFieldsInfo,
  populateFieldsInfoFromGlobalDTO,
  populateFieldsInfoFromSummary,
} = sweepFieldsInfoSlice.actions;

export const selectSweepFieldsInfo = (state: RootState) =>
  state[sweepFieldsInfoSlice.name].sweepFieldsInfo;

export default sweepFieldsInfoSlice.reducer;
